/** Typed calls to the Kept API. Errors carry the server's message where it gave one. */
import type { PartyRef, RuleBody } from '@kept/schedule';
import type { CreatePlan, CreateRule, PolicyBody, PolicySource } from '@kept/shared';

export interface Party {
  id: string;
  label: string;
  role: 'mother' | 'father' | 'other';
}
export interface Child {
  id: string;
  name: string;
  birthDate: string | null;
}
export interface SchoolBreak {
  id: string;
  kind: string;
  lastSchoolDay: string;
  firstDayBack: string;
}
export interface Plan {
  id: string;
  name: string;
  timeZone: string;
  defaultPartyId: string | null;
  measuredPartyId: string | null;
  statedShares: Record<string, number> | null;
  exchangeLocation: string | null;
  parties: Party[];
  children: Child[];
  schoolBreaks: SchoolBreak[];
}
export interface RuleVersion {
  id: string;
  ruleId: string;
  version: number;
  type: RuleBody['type'];
  clause: string;
  tier: number | null;
  effectiveFrom: string;
  ended: boolean;
  params: Record<string, unknown>;
  reason: string | null;
  createdAt: string;
}
export interface PolicyVersion {
  id: string;
  policyId: string;
  version: number;
  kind: PolicyBody['kind'];
  source: PolicySource;
  effectiveFrom: string;
  ended: boolean;
  params: Record<string, unknown>;
  reason: string | null;
}
export interface RuleRef {
  id: string;
  version: number;
  clause: string;
}
export interface Expanded {
  timeZone: string;
  from: string;
  to: string;
  intervals: { start: string; end: string; party: string; rule: RuleRef | null; overridden: RuleRef[] }[];
  visits: { start: string; end: string }[];
  hours: Record<string, number>;
  share: Record<string, number>;
}
export interface Me {
  user: { id: string; name: string; email: string };
  setup: { passkey: boolean; twoFactor: boolean };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      data?.message ??
      (data?.issues ? data.issues.map((i: { message: string }) => i.message).join(' ') : `Request failed (${res.status}).`);
    throw new ApiError(res.status, data?.error ?? 'error', message);
  }
  return data as T;
}

/** The rule body the API stores, rebuilt from a stored version. */
export function bodyOf(v: RuleVersion): RuleBody {
  return { ...(v.params as object), type: v.type } as RuleBody;
}

export const api = {
  me: () => call<Me>('GET', '/api/me'),
  plans: () => call<Plan[]>('GET', '/api/plans'),
  plan: (id: string) => call<Plan>('GET', `/api/plans/${id}`),
  createPlan: (input: CreatePlan) => call<Plan>('POST', '/api/plans', input),
  updatePlan: (id: string, input: Partial<Pick<Plan, 'name' | 'timeZone' | 'exchangeLocation'>>) =>
    call<Plan>('PATCH', `/api/plans/${id}`, input),
  rules: (planId: string) => call<RuleVersion[]>('GET', `/api/plans/${planId}/rules`),
  addRule: (planId: string, input: CreateRule) => call<RuleVersion>('POST', `/api/plans/${planId}/rules`, input),
  addRuleVersion: (planId: string, ruleId: string, input: CreateRule | { effectiveFrom: string; ended: true; reason?: string }) =>
    call<RuleVersion>('POST', `/api/plans/${planId}/rules/${ruleId}/versions`, input),
  policies: (planId: string) => call<PolicyVersion[]>('GET', `/api/plans/${planId}/policies`),
  addPolicy: (planId: string, input: { effectiveFrom: string; source: PolicySource; policy: PolicyBody; reason?: string }) =>
    call<PolicyVersion>('POST', `/api/plans/${planId}/policies`, input),
  addPolicyVersion: (
    planId: string,
    policyId: string,
    input: { effectiveFrom: string; source: PolicySource; policy: PolicyBody; reason?: string } | { effectiveFrom: string; ended: true; reason?: string },
  ) => call<PolicyVersion>('POST', `/api/plans/${planId}/policies/${policyId}/versions`, input),
  addBreak: (planId: string, input: Omit<SchoolBreak, 'id'>) => call<SchoolBreak>('POST', `/api/plans/${planId}/school-breaks`, input),
  deleteBreak: (planId: string, breakId: string) => call<null>('DELETE', `/api/plans/${planId}/school-breaks/${breakId}`),
  expand: (planId: string, from: string, to: string) =>
    call<Expanded>('GET', `/api/plans/${planId}/expand?from=${from}&to=${to}`),
};

export type { PartyRef };
